import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cancel as apiCancel,
  getEvents,
  getMethods,
  getSession,
  selectMethod,
  GoneError,
} from './api';
import type { CheckoutEvent, CheckoutSession, Method } from './api';
import { emit, init as initPostMessage } from './postMessage';
import MethodPicker from './components/MethodPicker';
import DepositScreen from './components/DepositScreen';
import StatusBadge from './components/StatusBadge';

type Phase =
  | 'loading'
  | 'method_picker'
  | 'deposit'
  | 'terminal'
  | 'error';

const TERMINAL_STATUSES = new Set(['settled', 'expired', 'cancelled']);

function isTerminal(s: CheckoutSession['status']): boolean {
  return TERMINAL_STATUSES.has(s);
}

// Truthiness, not `=== null`. The server omits chain_id/asset_id/address
// entirely until a method is chosen, so they arrive as `undefined` --
// and `undefined === null` is false. The old strict check therefore
// reported "method already chosen" for every fresh session, skipped the
// picker, and routed to a deposit screen with no address to render.
function needsMethod(s: CheckoutSession): boolean {
  return !s.chain_id || !s.asset_id || !s.address;
}

function nextPhase(s: CheckoutSession): Phase {
  if (isTerminal(s.status)) return 'terminal';
  if (needsMethod(s)) return 'method_picker';
  return 'deposit';
}

function syntheticExpired(): CheckoutSession {
  const now = new Date().toISOString();
  return {
    id: '',
    status: 'expired',
    client_uid: '',
    asset_decimals: 0,
    created_at: now,
    expires_at: now,
    updated_at: now,
  };
}

export default function App() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [methods, setMethods] = useState<Method[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const emittedSettledRef = useRef(false);
  const closedTimerRef = useRef<number | null>(null);

  const goTerminal = useCallback((s: CheckoutSession) => {
    setSession(s);
    setPhase('terminal');
  }, []);

  useEffect(() => {
    let alive = true;
    initPostMessage();

    (async () => {
      try {
        const s = await getSession();
        if (!alive) return;
        setSession(s);
        emit({ type: 'opened', session_id: s.id });

        if (isTerminal(s.status)) {
          setPhase('terminal');
          return;
        }

        if (needsMethod(s)) {
          const m = await getMethods();
          if (!alive) return;
          setMethods(m.items);
          setPhase('method_picker');
        } else {
          setPhase('deposit');
        }
      } catch (e) {
        if (!alive) return;
        if (e instanceof GoneError) {
          goTerminal(syntheticExpired());
          return;
        }
        setError(e instanceof Error ? e.message : 'Failed to load checkout');
        setPhase('error');
      }
    })();

    return () => {
      alive = false;
    };
  }, [goTerminal]);

  useEffect(() => {
    if (phase !== 'deposit' || !session) return;

    let alive = true;
    let timerId: number | null = null;

    // A transition carries only a status. There is no `type` field on the
    // wire, and reading one yielded undefined for every event -- so no
    // outbound message was ever emitted and the host page heard nothing.
    const applyEvents = (events: CheckoutEvent[]) => {
      for (const ev of events) {
        const common = {
          session_id: session.id,
          chain_id: session.chain_id,
          asset_id: session.asset_id,
        };
        if (ev.status === 'detected') {
          emit({
            type: 'detected',
            ...common,
            transaction_id: ev.matched_transaction_id,
          });
        } else if (ev.status === 'settled') {
          emit({
            type: 'settled',
            ...common,
            transaction_id: ev.matched_transaction_id,
            expected_amount: session.expected_amount,
            asset_symbol: session.asset_symbol,
          });
        } else if (ev.status === 'expired') {
          emit({ type: 'expired', session_id: session.id });
        } else if (ev.status === 'cancelled') {
          emit({ type: 'cancelled', session_id: session.id });
        }

        if (isTerminal(ev.status) || ev.status === 'detected') {
          setSession((prev) => (prev ? { ...prev, status: ev.status } : prev));
        }
        if (isTerminal(ev.status)) {
          setPhase('terminal');
        }
      }
    };

    const poll = async () => {
      if (!alive) return;
      try {
        const env = await getEvents(cursorRef.current ?? undefined);
        // `now` is the server clock to echo back as `since`. Reading a
        // `cursor` that does not exist sent since=undefined every poll,
        // so every tick replayed the whole history from the beginning.
        cursorRef.current = env.now;
        applyEvents(env.events);
      } catch (e) {
        if (e instanceof GoneError) {
          goTerminal({ ...session, status: 'expired' });
          return;
        }
        // Transient errors: swallow, next tick retries.
      }
      if (!alive) return;
      const delay = document.hidden ? 10_000 : 3_000;
      timerId = window.setTimeout(poll, delay);
    };

    timerId = window.setTimeout(poll, 3_000);

    return () => {
      alive = false;
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [phase, session, goTerminal]);

  useEffect(() => {
    if (phase !== 'terminal' || !session) return;

    if (closedTimerRef.current !== null) {
      window.clearTimeout(closedTimerRef.current);
      closedTimerRef.current = null;
    }

    const emitClosed = () =>
      emit({ type: 'closed', session_id: session.id });

    if (session.status === 'settled') {
      if (!emittedSettledRef.current) {
        emittedSettledRef.current = true;
        emit({
          type: 'settled',
          session_id: session.id,
          chain_id: session.chain_id,
          asset_id: session.asset_id,
          transaction_id: session.matched_transaction_id,
          expected_amount: session.expected_amount,
          asset_symbol: session.asset_symbol,
        });
      }
      closedTimerRef.current = window.setTimeout(emitClosed, 5000);
    } else if (session.status === 'expired') {
      closedTimerRef.current = window.setTimeout(emitClosed, 2000);
    } else if (session.status === 'cancelled') {
      emitClosed();
    }

    return () => {
      if (closedTimerRef.current !== null) {
        window.clearTimeout(closedTimerRef.current);
        closedTimerRef.current = null;
      }
    };
  }, [phase, session]);

  const handleSelect = useCallback(async (method: Method) => {
    try {
      const updated = await selectMethod({
        chain_id: method.chain_id,
        asset_id: method.asset_id,
      });
      setSession(updated);
      emit({
        type: 'method_selected',
        session_id: updated.id,
        chain_id: method.chain_id,
        asset_id: method.asset_id,
      });
      setPhase(nextPhase(updated));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to select payment method',
      );
    }
  }, []);

  const handleCancel = useCallback(async () => {
    if (!session) return;
    setCancelling(true);
    try {
      await apiCancel();
      goTerminal({ ...session, status: 'cancelled' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  }, [session, goTerminal]);

  const handleExpire = useCallback(() => {
    if (!session) return;
    goTerminal({ ...session, status: 'expired' });
  }, [session, goTerminal]);

  return (
    <main className="app">
      {phase === 'loading' && (
        <div className="spinner" aria-label="Loading checkout" />
      )}

      {phase === 'method_picker' && (
        <MethodPicker methods={methods} onSelect={handleSelect} />
      )}

      {phase === 'deposit' && session && (
        <DepositScreen
          session={session}
          onExpire={handleExpire}
          onCancel={handleCancel}
          cancelling={cancelling}
        />
      )}

      {phase === 'terminal' && session && (
        <div className="card">
          <div className="status-row">
            <StatusBadge status={session.status} />
          </div>
          <h2 className="heading">
            {session.status === 'settled' && 'Payment confirmed'}
            {session.status === 'expired' && 'Session expired'}
            {session.status === 'cancelled' && 'Payment cancelled'}
          </h2>
          <p className="subheading">
            {session.status === 'settled' &&
              'Thanks — you can close this window.'}
            {session.status === 'expired' &&
              'This checkout session is no longer active.'}
            {session.status === 'cancelled' &&
              'The payment was cancelled. No funds were moved.'}
          </p>
          {error && <p className="error">{error}</p>}
        </div>
      )}

      {phase === 'error' && (
        <div className="card">
          <h2 className="heading">Something went wrong</h2>
          <p className="error">{error ?? 'Unknown error'}</p>
        </div>
      )}
    </main>
  );
}
